export default interface FetchState {
  isFetching: boolean;
  fetchRequired: boolean;
  fetchFailed: number;
  fetchedAtLeastOnce: boolean;
}

const MAX_FETCH_ATTEMPTS = 10;
const DELAY_FETCH_MULTIPLIER = 1000;

export function dontFetch(): FetchState {
  return { isFetching: false, fetchRequired: false, fetchFailed: 0, fetchedAtLeastOnce: false };
}

export function requireFetch(fetchState?: FetchState): FetchState {
  if (!fetchState) {
    return { isFetching: false, fetchRequired: true, fetchFailed: 0, fetchedAtLeastOnce: false };
  }

  if (fetchState.fetchFailed > MAX_FETCH_ATTEMPTS) {
    throw Error('Data fetch failed!');
  }

  return { ...fetchState, fetchRequired: true };
}

export function startFetch(fetchState?: FetchState): FetchState {
  if (fetchState && !fetchState.fetchRequired) {
    throw Error('No fetch required!');
  }

  if (!fetchState) {
    return { isFetching: true, fetchRequired: false, fetchFailed: 0, fetchedAtLeastOnce: false };
  }

  return { ...fetchState, isFetching: true, fetchRequired: false };
}

export function failFetch(fetchState?: FetchState): FetchState {
  if (!fetchState) {
    throw Error('Cannot fail a non-existent fetch state');
  }
  if (!fetchState.isFetching) {
    throw Error('No fetch in progress!');
  }

  return { ...fetchState, isFetching: false, fetchRequired: true, fetchFailed: fetchState.fetchFailed + 1 };
}

export function completeFetch(fetchState?: FetchState): FetchState {
  if (!fetchState) {
    throw Error('Cannot complete a non-existent fetch state');
  }
  if (!fetchState.isFetching) {
    throw Error('No fetch in progress!');
  }

  return { ...fetchState, isFetching: false, fetchFailed: 0, fetchedAtLeastOnce: true };
}

export function shouldFetch(fetchState?: FetchState): boolean {
  return Boolean(!fetchState || (!fetchState.isFetching && fetchState.fetchRequired));
}

export function waitBeforeFetch(fetchState: FetchState | undefined): number {
  const fetchFailed = fetchState ? fetchState.fetchFailed : 0;
  const milliseconds = fetchFailed * DELAY_FETCH_MULTIPLIER;
  return milliseconds;
}

export function wasJustFetched(previousFetchState?: FetchState, currentFetchState?: FetchState): boolean {
  return Boolean(
    previousFetchState &&
    currentFetchState &&
    previousFetchState.isFetching &&
    !currentFetchState.isFetching &&
    currentFetchState.fetchFailed === previousFetchState.fetchFailed
  );
}
